import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchServices } from "../../api/servicesAPI";
import { placeholderImage, isValidUrl } from "../invalidImage";

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getServices = async () => {
      try {
        const servicesData = await fetchServices();
        if (Array.isArray(servicesData)) {
          setServices(servicesData);
        } else {
          throw new Error("Services data is not an array");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    getServices();
  }, []);

  return (
    <section
      id="OurServices"
      className="relative py-20 md:py-28 bg-slate-50 overflow-hidden"
    >
      {/* Decorative blur orbs */}
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-brand-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-eco-200/30 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-4xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-eco-50 text-eco-700 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-eco-500" />
            Layanan Kami
          </span>
          <h2 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-5">
            Solusi Komprehensif untuk{" "}
            <span className="text-gradient">Air dan Lingkungan</span>
          </h2>
          <p className="font-overpass text-base lg:text-lg text-slate-600 leading-relaxed">
            Kami menyediakan layanan komprehensif dalam pengolahan air bersih
            dan limbah cair, mencakup operasi, pemeliharaan, serta penerapan
            teknologi modern untuk menjamin kualitas, efisiensi, dan
            keberlanjutan.
          </p>
        </motion.div>

        {loading && (
          <div className="grid gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[420px] rounded-3xl bg-gradient-to-r from-slate-200 to-slate-100 animate-pulse-soft"
              />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 py-8">{error}</div>
        )}

        <div className="flex flex-col gap-8">
          {services.map((service, idx) => {
            const isReverse = service.id % 2 === 0;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: idx * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative rounded-3xl overflow-hidden shadow-medium hover:shadow-strong transition-shadow duration-500"
              >
                <div className="relative h-[420px] sm:h-[460px] lg:h-[480px] w-full">
                  <img
                    src={
                      isValidUrl(service.image)
                        ? service.image
                        : placeholderImage
                    }
                    alt={service.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  {/* Gradient overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-${
                      isReverse ? "l" : "r"
                    } from-slate-950/95 via-slate-900/60 to-transparent`}
                  />
                  {/* Accent gradient */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-${
                      isReverse ? "l" : "r"
                    } from-brand-900/40 to-transparent`}
                  />

                  {/* Content */}
                  <div
                    className={`absolute inset-0 flex flex-col justify-end p-6 sm:p-10 lg:p-14 ${
                      isReverse ? "items-end text-right" : "items-start text-left"
                    }`}
                  >
                    <div className="max-w-2xl">
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-white/10 text-white border border-white/30 backdrop-blur-md mb-4`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-eco-400" />
                        Service #{String(idx + 1).padStart(2, "0")}
                      </span>

                      <h3 className="font-oswald text-white text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 leading-tight drop-shadow-lg">
                        {service.name}
                      </h3>

                      <p className="font-overpass text-white/90 text-sm sm:text-base lg:text-lg leading-relaxed line-clamp-4 lg:line-clamp-5">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  {/* Decorative corner */}
                  <div
                    className={`absolute top-0 ${
                      isReverse ? "left-0" : "right-0"
                    } w-24 h-24 bg-gradient-to-br from-brand-500/30 to-eco-500/30 blur-2xl`}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
